import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Biography = () => {
  return (
    <div className="min-h-screen bg-white pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/about"
          className="inline-flex items-center mb-8 font-semibold transition-opacity hover:opacity-80"
          style={{ color: '#5E7F8A' }}
          data-testid="back-to-about"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to About
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#5E7F8A' }}>
            Meet Jeanine Zalduendo
          </h1>
          <p className="text-xl" style={{ color: '#9BB2A2' }}>
            Mediator | Harvard-Trained Attorney | Advocate for Families in Transition
          </p>
        </div>

        {/* Photo */}
        <div className="mb-12 flex justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_2b8b53d6-f420-42d5-9708-89a0fcd9ca22/artifacts/7dwb187p_replicate-prediction-ja0csvspjnrmw0cwqgnv951y3w.jpeg" 
            alt="Jeanine Zalduendo"
            className="w-80 h-80 object-cover rounded-lg shadow-xl"
          />
        </div>

        {/* Bio Content */}
        <div className="prose prose-lg max-w-none" style={{ color: '#5E7F8A' }}>
          <p className="text-lg leading-relaxed mb-6">
            Some people find their way to mediation through a career path. I found mine through life itself.
          </p>

          <p className="text-lg leading-relaxed mb-6">
            I am a Harvard Law School graduate and a commercial litigator with more than twenty years of experience navigating some of the most complex, high-stakes disputes in the country. I have spent my career inside the adversarial system — and over time, I came to understand something important: winning in court rarely means healing. It rarely means peace. For families caught in the upheaval of divorce or separation, the courtroom can leave everyone — especially children — more wounded than when they walked in.
          </p>

          <p className="text-lg leading-relaxed mb-6">
            That understanding deepened when my own life changed in ways I never anticipated. I became a widow when my son was just ten months old. In the years since, I have learned firsthand what it means to face grief, uncertainty, and the overwhelming challenge of rebuilding — all while trying to be the steadfast, loving parent your child needs you to be, even when your own heart is still finding its footing.
          </p>

          <p className="text-lg leading-relaxed mb-6">
            I know what it feels like to navigate an unclear path, to make decisions about the future when the ground beneath you still feels unsteady. That experience is not something I set aside when I step into the mediation room. It is something I carry with me — as empathy, as patience, and as a deep commitment to helping others move forward with clarity and dignity.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-6" style={{ color: '#5E7F8A' }}>
            From the Courtroom to the Mediation Table
          </h2>

          <p className="text-lg leading-relaxed mb-6">
            My litigation background is not incidental to this work — it is central to it. After more than two decades representing clients in high-stakes civil disputes, I understand the legal landscape of divorce and family law with precision. I know how courts think, how attorneys negotiate, and what the adversarial path truly costs — in time, in money, and in emotional toll.
          </p>

          <p className="text-lg leading-relaxed mb-6">
            That knowledge allows me to guide clients through mediation with both legal fluency and genuine insight into what is at stake. To deepen my expertise in family mediation specifically, I have completed over 200 hours of specialized training, equipping me with the tools and frameworks to help couples and families resolve even the most emotionally charged issues — from property and finances to parenting plans and co-parenting communication — in a way that is fair, thoughtful, and durable.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-6" style={{ color: '#5E7F8A' }}>
            Why Paix?
          </h2>

          <p className="text-lg leading-relaxed mb-6">
            Paix is the French word for peace — and it is the word that best captures what I hope every client walks away with. Not the absence of conflict, but something more meaningful: a sense of resolution, of possibility, of being ready to begin the next chapter.
          </p>

          <p className="text-lg leading-relaxed mb-6">
            Relationships change. Families evolve. My role is not to make that transition painless — some pain is inevitable — but to make it navigable. To help you find your footing, protect what matters most to you, and move forward with your dignity intact.
          </p>

          <p className="text-lg leading-relaxed mb-6">
            If you are facing a divorce or family transition and wondering whether there is a better way than litigation, I would be honored to talk with you. You deserve a mediator who brings both the legal expertise to understand your situation fully and the human experience to understand what you are going through.
          </p>

          <p className="text-xl font-semibold italic mb-10" style={{ color: '#9BB2A2' }}>
            Let's find your peace together.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-6" style={{ color: '#5E7F8A' }}>
            Credentials & Training
          </h2>

          <ul className="list-none space-y-3 mb-10">
            <li className="flex items-start">
              <span className="font-semibold mr-2">Education:</span>
              <span>J.D., Harvard Law School (2005) | B.A. with Honors, Psychology, University of Chicago (2002)</span>
            </li>
            <li className="flex items-start">
              <span className="font-semibold mr-2">Mediation Training:</span>
              <span>200+ hours of specialized family and divorce mediation training</span>
            </li>
            <li className="flex items-start">
              <span className="font-semibold mr-2">Litigation Experience:</span>
              <span>20+ years as a commercial litigator in state and federal courts nationwide</span>
            </li>
            <li className="flex items-start">
              <span className="font-semibold mr-2">Recognition:</span>
              <span>Los Angeles Business Journal Leaders of Influence (2022, 2024)</span>
            </li>
          </ul>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gray-50 rounded-lg p-10 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#5E7F8A' }}>
            Ready to Take the Next Step?
          </h2>
          <p className="text-lg mb-6" style={{ color: '#5E7F8A' }}>
            Schedule a free consultation to discuss how mediation can help your family.
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 py-4 rounded-md text-white font-semibold transition-all hover:shadow-lg"
            style={{ backgroundColor: '#9BB2A2' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#8FA596'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9BB2A2'}
            data-testid="contact-cta"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Biography;
